import { useEffect, useState } from 'react';
import {
    endOfMinute,
    isAfter,
    isBefore,
    startOfDay,
    startOfMonth,
} from 'date-fns';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Popover } from '@/components/Popover';
import { createDateTime } from '@/utils/date';
import { DateTimeMode } from '@/utils/query-filters';
import { DateTimeInput } from './DateTimeInput';
import { QuickSelectButtons } from './QuickSelectButtons';
import { RangeCalendar } from './RangeCalendar';
import { createNarrowWeekdayFormatter } from './utils';

export interface DateTimeRangePickerResult {
    startTime?: Date;
    endTime?: Date;
    mode: DateTimeMode;
}

interface DateTimeRangePickerProps {
    initialStartDate?: Date;
    initialEndDate?: Date;
    initialMode?: DateTimeMode;
    allowFutureDates?: boolean;
    earliestDate?: Date;
    onStartTimeChange?: (date?: Date) => void;
    onEndTimeChange?: (date?: Date) => void;
    onRangeChange?: (range: { startTime?: Date; endTime?: Date }) => void;
    onApply?: (range: DateTimeRangePickerResult) => void;
    onClear?: () => void;
    onClose?: () => void;
    immediatelyApply?: boolean;
}

interface SelectionBounds {
    allowFutureDates: boolean;
    earliestDate?: Date;
}

const DEFAULT_START_TIME = '00:00';
const DEFAULT_END_TIME = '23:59';

function clampToBounds(
    date: Date | undefined,
    { allowFutureDates, earliestDate }: SelectionBounds,
): Date | undefined {
    // A malformed URL date reaches here as an Invalid Date. isAfter/isBefore
    // both return false for it, so without this guard it would flow through to
    // RangeCalendar's controlled month.
    if (!date || Number.isNaN(date.getTime())) return undefined;
    if (!allowFutureDates) {
        const now = new Date();
        if (isAfter(date, now)) return now;
    }
    if (earliestDate && isBefore(date, earliestDate)) return earliestDate;
    return date;
}

// Picking a day in the calendar means the whole day. Carrying the previous
// value's time over would silently drop the hours before it.
function toRangeStart(day: Date, bounds: SelectionBounds): Date | undefined {
    return clampToBounds(createDateTime(day, DEFAULT_START_TIME), bounds);
}

function toRangeEnd(day: Date, bounds: SelectionBounds): Date | undefined {
    return clampToBounds(createDateTime(day, DEFAULT_END_TIME, true), bounds);
}

export function DateTimeRangePicker({
    initialStartDate,
    initialEndDate,
    initialMode = DateTimeMode.CUSTOM,
    allowFutureDates = false,
    earliestDate,
    onStartTimeChange,
    onEndTimeChange,
    onRangeChange,
    onApply,
    onClear,
    onClose,
    immediatelyApply = false,
}: DateTimeRangePickerProps) {
    const { t, i18n } = useTranslation();

    const bounds: SelectionBounds = { allowFutureDates, earliestDate };

    // Seeded once per mount. Both hosts render this picker inside a Radix
    // popover that unmounts on close, so it always opens with current props.
    // Syncing from props on every render instead would loop, because callers
    // build the initial dates inline with new Date().
    const [storedStartDateTime, setStartDateTime] = useState(() =>
        clampToBounds(initialStartDate, bounds),
    );
    const [storedEndDateTime, setEndDateTime] = useState(() =>
        clampToBounds(initialEndDate, bounds),
    );
    const [storedCalendarMonth, setCalendarMonth] = useState(
        () => clampToBounds(initialStartDate, bounds) ?? new Date(),
    );
    const [selectedMode, setSelectedMode] = useState(initialMode);

    // Clamped on read, not only on write. earliestDate is fetched, so it can
    // arrive after a value was already picked; deriving it here re-clamps that
    // value instead of leaving it outside the retention window. Idempotent for
    // in-range values, so an in-progress edit is untouched.
    const startDateTime = clampToBounds(storedStartDateTime, bounds);
    const endDateTime = clampToBounds(storedEndDateTime, bounds);
    const calendarMonth =
        clampToBounds(storedCalendarMonth, bounds) ?? new Date();

    const selectedRange: DateRange = {
        from: startDateTime,
        to: endDateTime,
    };

    // A host can clear the range from outside while this picker stays mounted —
    // LogsTab's filters menu deletes the from/to URL params, and its date
    // popover is a sibling menu item. Without this the cleared range would keep
    // showing and could be reapplied. Gated on a boolean so it cannot loop on
    // the inline new Date() instances callers build every render.
    const hasInitialRange = !!initialStartDate || !!initialEndDate;

    useEffect(() => {
        if (hasInitialRange) return;
        resetSelection();
    }, [hasInitialRange]);

    const latestSelectableDate = allowFutureDates ? undefined : new Date();

    // Compared as instants, not days: both endpoints carry a time, so
    // 18:00 -> 09:00 on the same day is inverted even though the days match.
    const hasValidRange =
        !!startDateTime &&
        !!endDateTime &&
        !isAfter(startDateTime, endDateTime);

    const hasAnythingToClear = !!startDateTime || !!endDateTime;

    const disabledDays =
        allowFutureDates && !earliestDate
            ? undefined
            : (date: Date) =>
                  (!allowFutureDates &&
                      isAfter(startOfDay(date), startOfDay(new Date()))) ||
                  (!!earliestDate &&
                      isBefore(startOfDay(date), startOfDay(earliestDate)));

    // startMonth / endMonth stop calendar navigation at the bounds, and every
    // date written to calendarMonth is already clamped, so no effect is needed
    // to pull the month back into range.
    const navigationStartMonth = earliestDate
        ? startOfMonth(earliestDate)
        : undefined;

    const navigationEndMonth = allowFutureDates ? undefined : new Date();

    function notifyCallbacks(from?: Date, to?: Date) {
        onStartTimeChange?.(from);
        onEndTimeChange?.(to);
        onRangeChange?.({ startTime: from, endTime: to });
    }

    function emitApply(from: Date, to: Date, mode: DateTimeMode) {
        if (!onApply) return;
        onApply({
            startTime: clampToBounds(from, bounds),
            // The field only carries minutes, so the end is stretched to cover
            // its final minute. Rounded before clamping, so a range ending
            // "now" still cannot reach into the future.
            endTime: clampToBounds(endOfMinute(to), bounds),
            mode,
        });
    }

    function applySelection(from?: Date, to?: Date) {
        setSelectedMode(DateTimeMode.CUSTOM);
        setStartDateTime(from);
        setEndDateTime(to);
        if (from) setCalendarMonth(from);
        notifyCallbacks(from, to);
    }

    function handleSelect(range: DateRange | undefined, triggerDate: Date) {
        if (!immediatelyApply) {
            if (!range) return;
            const from = range.from
                ? toRangeStart(range.from, bounds)
                : undefined;
            const to = range.to ? toRangeEnd(range.to, bounds) : undefined;
            applySelection(from, to);
            return;
        }

        const awaitingEnd = !!startDateTime && !endDateTime;
        if (!awaitingEnd) {
            applySelection(toRangeStart(triggerDate, bounds), undefined);
            return;
        }

        const start = startDateTime as Date;
        const [fromDay, toDay] = isBefore(triggerDate, start)
            ? [triggerDate, start]
            : [start, triggerDate];
        const from = toRangeStart(fromDay, bounds);
        const to = toRangeEnd(toDay, bounds);
        applySelection(from, to);
        if (from && to) emitApply(from, to, DateTimeMode.CUSTOM);
    }

    function handleQuickSelect(
        days: number,
        hours: number | undefined,
        mode: DateTimeMode,
    ) {
        const end = new Date();
        const start = new Date();
        start.setDate(days === 0 ? start.getDate() : start.getDate() - days);
        start.setHours(start.getHours() - (hours ?? 0));
        // A preset can reach past the retention window, so it is clamped too.
        const normalizedStart = clampToBounds(
            days > 1 ? startOfDay(start) : start,
            bounds,
        ) as Date;

        setSelectedMode(mode);
        setStartDateTime(normalizedStart);
        setEndDateTime(end);
        setCalendarMonth(normalizedStart);
        notifyCallbacks(normalizedStart, end);

        if (immediatelyApply) {
            emitApply(normalizedStart, end, mode);
        }
    }

    function resetSelection() {
        setStartDateTime(undefined);
        setEndDateTime(undefined);
        setCalendarMonth(new Date());
    }

    function handleClear() {
        resetSelection();
        notifyCallbacks(undefined, undefined);
        onClear?.();
    }

    function handleApply() {
        if (!startDateTime || !endDateTime) return;
        emitApply(startDateTime, endDateTime, selectedMode);
        onClose?.();
    }

    function handleStartChange(next?: Date) {
        const from = clampToBounds(next, bounds);
        const invertsRange =
            !!from && !!endDateTime && isAfter(from, endDateTime);
        applySelection(from, invertsRange ? undefined : endDateTime);
    }

    function handleEndChange(next?: Date) {
        const to = clampToBounds(next, bounds);
        const invertsRange =
            !!to && !!startDateTime && isAfter(startDateTime, to);
        const from = invertsRange ? undefined : startDateTime;
        setSelectedMode(DateTimeMode.CUSTOM);
        setStartDateTime(from);
        setEndDateTime(to);
        if (to) setCalendarMonth(to);
        notifyCallbacks(from, to);
    }

    function commitInputChange() {
        if (!immediatelyApply) return;
        if (!startDateTime || !endDateTime) return;
        if (isAfter(startDateTime, endDateTime)) return;
        emitApply(startDateTime, endDateTime, selectedMode);
    }

    return (
        <Popover
            title={t('datePicker.title')}
            onClose={onClose}
            className="box-border w-full max-w-[324px] gap-6"
        >
            <div>
                <RangeCalendar
                    disabled={disabledDays}
                    startMonth={navigationStartMonth}
                    endMonth={navigationEndMonth}
                    selected={selectedRange}
                    onSelect={handleSelect}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    formatWeekdayName={createNarrowWeekdayFormatter(
                        i18n.language,
                    )}
                />

                <QuickSelectButtons onSelect={handleQuickSelect} />

                <div className="mt-6 flex flex-col gap-2">
                    <DateTimeInput
                        label={t('datePicker.start')}
                        value={startDateTime}
                        onChange={handleStartChange}
                        onCommit={commitInputChange}
                        minValue={earliestDate}
                        maxValue={latestSelectableDate}
                    />

                    <DateTimeInput
                        label={t('datePicker.end')}
                        value={endDateTime}
                        onChange={handleEndChange}
                        onCommit={commitInputChange}
                        minValue={earliestDate}
                        maxValue={latestSelectableDate}
                    />
                </div>

                {!immediatelyApply && (
                    <div className="mt-6 flex justify-between">
                        <Button
                            variant="ghost"
                            onClick={handleClear}
                            disabled={!hasAnythingToClear}
                        >
                            {t('datePicker.clear')}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleApply}
                            disabled={!hasValidRange}
                        >
                            {t('datePicker.applyFilter')}
                        </Button>
                    </div>
                )}
            </div>
        </Popover>
    );
}
